import { ApiMethod } from "../models/api-types.type";
import { useAuthContext } from "./use-auth-context.hook";

/** A figure over the period, beside the same figure for the period before. */
export interface Compared {
    value: number | null;
    previous: number | null;
    change: number | null;
}

export interface HomeSummary {
    period: { startDate: string; endDate: string };
    features: { shopperChat: boolean; adsManager: boolean; socialScheduler: boolean; contentStudio: boolean };
    attention: {
        /** Insight findings, as the Insights panel renders them. */
        findings: any[];
        ads: null | {
            suggestions: Array<{ uuid: string; title: string; severity: string; estimatedMonthlyImpact: number | null; currency: string | null }>;
            openCount: number;
            reconnect: Array<{ provider: string; accountName: string }>;
        };
        assistantUnanswered: number;
        social: null | {
            failedPosts: number;
            expiredAccounts: Array<{ platform: string; accountName: string }>;
        };
    };
    workspaces: {
        visualizer: null | { visitors: Compared; leads: Compared; roomUploads: Compared };
        assistant: null | { locked: boolean; conversations?: number; questions?: number; unanswered?: number; cartClicks?: number };
        ads: null | {
            locked: boolean;
            connected?: boolean;
            spend?: number;
            leads?: number;
            costPerLead?: number | null;
            currency?: string | null;
            previous?: { spend: number; leads: number; costPerLead: number | null } | null;
        };
        marketing: null | { scheduledNext7Days: number | null; emailsSent: Compared | null; imagesMade: Compared | null };
        business: null | { connected: boolean; revenue: number; orders: number; currency: string | null };
    };
}

export interface MarketingOverview {
    period: { startDate: string; endDate: string };
    social: null | {
        totals: { impressions: number; reach: number; likes: number; comments: number; shares: number; saves: number; clicks: number };
        avgEngagementRate: number;
        totalPublished: number;
        statusBreakdown: Record<string, number>;
        platformBreakdown: Record<string, number>;
    };
    upcoming: Array<{ uuid: string; platform: string; caption: string; scheduledAt: string; imageUrl: string | null }>;
    email: null | { sent: Compared; openRate: Compared; clickRate: Compared; series: Array<{ date: string; sent: number }> };
    studio: null | { images: Compared; creditsSpent: Compared };
    recentImages: Array<{ uuid: string; url: string; kind: string; projectUuid: string; createdAt: string }>;
}

const query = (params: Record<string, string | undefined>) => {
    const search = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => { if (v) search.set(k, v); });
    const text = search.toString();
    return text ? `?${text}` : '';
};

export type ConnectionKey = 'website' | 'visualizer' | 'shop' | 'social' | 'ads' | 'email' | 'brand' | 'competitors' | 'channels';
export type ConnectionState = 'connected' | 'attention' | 'missing' | 'locked';

/** One thing the vendor can connect, and whether it is. Read live from the service that owns it. */
export interface Connection {
    key: ConnectionKey;
    state: ConnectionState;
    /** What is connected, as it should read — "starkcarpet.com", "Instagram, Facebook". */
    detail?: string;
    count?: number;
}

/** The Home dashboard's summary, and the Marketing workspace's overview. */
export const useHomeApi = () => {
    const { sendAuthGuardedRequest } = useAuthContext();
    return {
        getSummary: () => sendAuthGuardedRequest(ApiMethod.GET, '/services/pms/home/summary') as Promise<HomeSummary>,
        getConnections: () =>
            (sendAuthGuardedRequest(ApiMethod.GET, '/services/pms/home/connections') as Promise<{ connections: Connection[] }>)
                .then((r: any) => ((r?.data ?? r)?.connections ?? []) as Connection[]),
        getMarketing: (range: { startDate: string; endDate: string }) =>
            sendAuthGuardedRequest(ApiMethod.GET, '/services/pms/home/marketing' + query(range)) as Promise<MarketingOverview>,
    };
};
